import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import {
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Pause,
  RefreshCw,
  CircleSlash,
  HelpCircle,
  MessageSquareMore
} from 'lucide-react'
import type { SessionStatus } from '@/lib/types'

/**
 * Props for SessionStatusBadge component
 */
export interface SessionStatusBadgeProps {
  /** Current session status */
  status: SessionStatus
  /** Size variant */
  size?: 'sm' | 'default' | 'lg'
  /** Additional CSS classes */
  className?: string
}

/**
 * Configuration for each status type
 */
interface StatusConfig {
  label: string
  icon: React.ElementType
  className: string
  ariaLabel: string
  animate?: boolean
}

/**
 * Status configuration mapping
 */
const statusConfig: Record<SessionStatus, StatusConfig> = {
  Idle: {
    label: 'Idle',
    icon: HelpCircle,
    className: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
    ariaLabel: 'Session is idle'
  },
  Running: {
    label: 'Running',
    icon: Loader2,
    className: 'bg-primary/10 text-primary border-primary/20',
    ariaLabel: 'Session is running',
    animate: true
  },
  WaitingForInput: {
    label: 'Waiting',
    icon: MessageSquareMore,
    className: 'bg-violet-500/10 text-violet-600 border-violet-500/20',
    ariaLabel: 'Waiting for user input'
  },
  Completed: {
    label: 'Completed',
    icon: CheckCircle2,
    className: 'bg-green-500/10 text-green-600 border-green-500/20',
    ariaLabel: 'Session completed successfully'
  },
  Failed: {
    label: 'Failed',
    icon: XCircle,
    className: 'bg-red-500/10 text-red-600 border-red-500/20',
    ariaLabel: 'Session failed'
  },
  Timeout: {
    label: 'Timeout',
    icon: AlertTriangle,
    className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    ariaLabel: 'Session timed out'
  },
  // Background session statuses
  Queued: {
    label: 'Queued',
    icon: Clock,
    className: 'bg-sky-500/10 text-sky-700 border-sky-500/20',
    ariaLabel: 'Session is queued for processing'
  },
  Paused: {
    label: 'Paused',
    icon: Pause,
    className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    ariaLabel: 'Session is paused'
  },
  Resumable: {
    label: 'Resumable',
    icon: RefreshCw,
    className: 'bg-primary/10 text-primary border-primary/20',
    ariaLabel: 'Session can be resumed'
  },
  Cancelled: {
    label: 'Cancelled',
    icon: CircleSlash,
    className: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
    ariaLabel: 'Session was cancelled'
  }
}

/**
 * Size classes for the badge
 */
const sizeClasses = {
  sm: 'text-xs px-1.5 py-0.5 gap-1',
  default: 'text-xs px-2 py-1 gap-1.5',
  lg: 'text-sm px-2.5 py-1 gap-1.5'
}

/**
 * Icon sizes for each badge size
 */
const iconSizes = {
  sm: 'h-3 w-3',
  default: 'h-3.5 w-3.5',
  lg: 'h-4 w-4'
}

/**
 * SessionStatusBadge displays the current status of a session with appropriate
 * styling and icons. Supports all session statuses including background session
 * statuses like Queued, Paused, Resumable, and Cancelled.
 */
export function SessionStatusBadge({
  status,
  size = 'default',
  className
}: SessionStatusBadgeProps) {
  const config = statusConfig[status] ?? statusConfig.Idle
  const Icon = config.icon

  return (
    <Badge
      variant="outline"
      role="status"
      aria-label={config.ariaLabel}
      className={cn(
        'inline-flex items-center font-medium',
        sizeClasses[size],
        config.className,
        className
      )}
    >
      <Icon
        className={cn(
          iconSizes[size],
          config.animate && 'animate-spin'
        )}
      />
      <span>{config.label}</span>
    </Badge>
  )
}
